import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, X, MessageCircle } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { fmtBRL, fmtKg, fmtNum } from "@/components/ui/Primitives";

const CATS = [
  { k: "cx1",  label: "CX1" },
  { k: "cx2",  label: "CX2" },
  { k: "cx3",  label: "CX3" },
  { k: "cx3g", label: "CX3G" },
  { k: "cx4",  label: "CX4" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length <= 10 ? iso + "T00:00:00" : iso);
  if (isNaN(d.getTime())) return "—";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

function addDays(isoBase, days) {
  const d = new Date((isoBase || new Date().toISOString()).length <= 10 ? (isoBase || "") + "T00:00:00" : (isoBase || new Date().toISOString()));
  if (isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function RomaneioTerceiros() {
  const { id } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [company, setCompany] = useState(null);
  const [pallets, setPallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [{ data: i }, { data: c }, { data: pls }] = await Promise.all([
          api.get(`/invoices/${id}`),
          api.get(`/company`),
          api.get(`/pallets`, { params: { tipo_operacao: "terceiros", page_size: 500 } }),
        ]);
        setInv(i); setCompany(c);
        setPallets((pls || []).filter((p) => (p.itens || []).some((it) => it.invoice_id === id)));
      } catch (e) { setErr(formatErr(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const doPrint = () => window.print();

  const shareWhatsApp = () => {
    if (!inv) return;
    const CAT_MAP = { cx1: "CX1", cx2: "CX2", cx3: "CX3", cx3g: "CX3G", cx4: "CX4" };
    const linhasCat = CATS
      .map((c) => {
        const q = Number((benef[c.k] || {}).quantidade || 0);
        return q > 0 ? `- ${CAT_MAP[c.k]}: ${fmtNum(q, 0)} sc (${fmtKg(q * 20)})` : null;
      })
      .filter(Boolean)
      .join("\n");

    const linhaSacaria = inv.usa_sacaria_propria && sacosSacariaPropria > 0
      ? `\n+ Sacaria (${fmtNum(sacosSacariaPropria, 0)} sc × ${fmtBRL(precoSacaria)}) = *${fmtBRL(valorSacaria)}*`
      : "";

    const msg =
`*🧅 ROMANEIO — BENEFICIAMENTO A TERCEIROS*
${company?.nome_fantasia || company?.razao_social || "Cebolas Serenata"}

*Cliente:* ${inv.cliente_dono || "—"}
*NF entrada:* #${inv.nota_numero}
*Produto:* ${(inv.tipo_cebola || "—").toUpperCase()}
*Data beneficiamento:* ${fmtDate(dataBenef)}

━━━━━━━━━━━━━━━━━━
*SACOS BENEFICIADOS*
${linhasCat}
Total: *${fmtNum(totalSacos, 0)} sacos* (${fmtKg(pesoTeorico)})

━━━━━━━━━━━━━━━━━━
*PESAGEM*
Peso caminhão (entrada): ${fmtKg(pesoCam)}
Peso embalado: ${fmtKg(pesoTeorico)}
Quebra: *${quebraPct.toFixed(2)}%* (${fmtKg(quebraKg)})

━━━━━━━━━━━━━━━━━━
*FATURAMENTO*
Maquinação (${fmtNum(totalSacos, 0)} sc × ${fmtBRL(precoServico)}) = *${fmtBRL(valorMaquinacao)}*${linhaSacaria}

💰 *TOTAL A PAGAR: ${fmtBRL(valorTotal)}*
Vencimento: *${fmtDate(vencimento)}* (15 dias)

━━━━━━━━━━━━━━━━━━
📎 _Envio o romaneio em PDF em anexo para conferência._`;

    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (loading) return <div className="p-8 text-center text-ink-500">Carregando…</div>;
  if (err) return <div className="p-8"><div className="text-danger">{err}</div></div>;
  if (!inv) return <div className="p-8">NF não encontrada.</div>;
  if (inv.tipo_operacao !== "terceiros") {
    return <div className="p-8">Esta NF não é de beneficiamento a terceiros.</div>;
  }

  const benef = inv.beneficiamento || {};
  const totalSacos = CATS.reduce((a, c) => a + Number((benef[c.k] || {}).quantidade || 0), 0);
  const pesoTeorico = totalSacos * 20; // kg — cada saco ~20kg
  // Peso do caminhão = peso da entrada (aferido na balança na chegada)
  const pesoCam = Number(inv.peso_liquido) || 0;
  const quebraKg = Math.max(0, pesoCam - pesoTeorico);
  const quebraPct = pesoCam > 0 ? (quebraKg / pesoCam) * 100 : 0;

  const precoServico = Number(inv.preco_servico_sc || 0);
  const precoSacaria = Number(inv.preco_sacaria_sc || 0);
  const valorMaquinacao = totalSacos * precoServico;

  const sacosSacariaPropria = pallets
    .filter((p) => !p.ignorar_sacaria)
    .reduce((a, p) => a + Number(p.total_caixas || 0), 0);
  const valorSacaria = inv.usa_sacaria_propria ? sacosSacariaPropria * precoSacaria : 0;
  const valorTotal = valorMaquinacao + valorSacaria;

  const dataBenef = inv.data_beneficiamento || inv.data_entrada || "";
  const vencimento = addDays(dataBenef, 15);

  return (
    <div className="bg-white min-h-screen">
      {/* Barra topo (some na impressão) */}
      <div className="print:hidden bg-white border-b border-ink-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <button onClick={() => nav(-1)} className="p-2 hover:bg-stone-100 rounded flex items-center gap-2 text-sm" data-testid="close-romaneio">
          <X size={16} /> Fechar
        </button>
        <div className="text-sm text-ink-700 font-mono">Romaneio Terceiros — NF #{inv.nota_numero}</div>
        <div className="flex items-center gap-2">
          <button
            onClick={shareWhatsApp}
            className="px-3 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 flex items-center gap-2"
            title="Envia o resumo em texto via WhatsApp. Anexe o PDF (salvo do botão Imprimir) manualmente no chat."
            data-testid="share-whatsapp"
          >
            <MessageCircle size={16} /> WhatsApp
          </button>
          <button onClick={doPrint} className="px-4 py-2 rounded-md bg-purple-600 text-white text-sm font-semibold hover:opacity-90 flex items-center gap-2" data-testid="print-romaneio">
            <Printer size={16} /> Imprimir
          </button>
        </div>
      </div>

      {/* Documento A4 */}
      <div className="max-w-[210mm] mx-auto p-8 print:p-[10mm] print:max-w-none">
        {/* Cabeçalho empresa */}
        <div className="flex items-start justify-between border-b-2 border-purple-700 pb-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-purple-700 font-bold">Cebolas Serenata</div>
            <h1 className="text-xl font-black tracking-tight text-ink-900 mt-0.5">{company?.razao_social || company?.nome_fantasia || "—"}</h1>
            <div className="text-[11px] text-ink-600 mt-1">
              {company?.cnpj && <span>CNPJ: <span className="font-mono">{company.cnpj}</span></span>}
              {company?.endereco && <span> • {company.endereco}</span>}
            </div>
          </div>
          <div className="text-right">
            <div className="inline-block bg-purple-700 text-white px-3 py-1 rounded font-bold uppercase tracking-widest text-xs">Romaneio Beneficiamento a Terceiros</div>
            <div className="text-[11px] text-ink-500 mt-2">Emitido em <span className="font-mono">{fmtDate(new Date().toISOString())}</span></div>
          </div>
        </div>

        {/* Cliente + NF */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="p-3 rounded bg-purple-50 border border-purple-200">
            <div className="text-[10px] uppercase tracking-widest text-purple-700 font-bold mb-1">Cliente (Dono da Mercadoria)</div>
            <div className="font-bold text-lg text-ink-900" data-testid="cliente-dono">{inv.cliente_dono || "—"}</div>
            {inv.cliente_dono_cnpj && <div className="text-xs text-ink-600 font-mono mt-0.5">CNPJ: {inv.cliente_dono_cnpj}</div>}
          </div>
          <div className="p-3 rounded bg-stone-50 border border-stone-200">
            <div className="text-[10px] uppercase tracking-widest text-ink-500 font-bold mb-1">Nota Fiscal de Entrada</div>
            <div className="grid grid-cols-2 gap-y-0.5 text-sm">
              <div className="text-ink-500">Nº</div><div className="font-mono font-bold text-right">#{inv.nota_numero}</div>
              <div className="text-ink-500">Data entrada</div><div className="font-mono text-right">{fmtDate(inv.data_entrada)}</div>
              <div className="text-ink-500">Beneficiamento</div><div className="font-mono text-right">{fmtDate(dataBenef)}</div>
              <div className="text-ink-500">Produto</div><div className="text-right capitalize">{inv.tipo_cebola || "—"}</div>
            </div>
          </div>
        </div>

        {/* Beneficiamento — categorias */}
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest text-purple-700 font-bold mb-2">Beneficiamento — Sacos por Categoria</div>
          <table className="w-full text-sm border border-stone-300">
            <thead className="bg-purple-50">
              <tr>
                <th className="border border-stone-300 px-3 py-1.5 text-left text-[11px] uppercase tracking-wider text-purple-800">Categoria</th>
                <th className="border border-stone-300 px-3 py-1.5 text-right text-[11px] uppercase tracking-wider text-purple-800">Sacos</th>
                <th className="border border-stone-300 px-3 py-1.5 text-right text-[11px] uppercase tracking-wider text-purple-800">Peso (kg)</th>
                <th className="border border-stone-300 px-3 py-1.5 text-right text-[11px] uppercase tracking-wider text-purple-800">% do total</th>
              </tr>
            </thead>
            <tbody>
              {CATS.map((c) => {
                const q = Number((benef[c.k] || {}).quantidade || 0);
                const kg = q * 20;
                const pct = totalSacos > 0 ? (q / totalSacos) * 100 : 0;
                return (
                  <tr key={c.k} className={q === 0 ? "opacity-50" : ""}>
                    <td className="border border-stone-300 px-3 py-1 font-semibold">{c.label}</td>
                    <td className="border border-stone-300 px-3 py-1 text-right font-mono">{fmtNum(q, 0)}</td>
                    <td className="border border-stone-300 px-3 py-1 text-right font-mono">{fmtKg(kg)}</td>
                    <td className="border border-stone-300 px-3 py-1 text-right font-mono">{pct.toFixed(1)}%</td>
                  </tr>
                );
              })}
              <tr className="bg-purple-50 font-bold">
                <td className="border border-stone-300 px-3 py-1.5">TOTAL</td>
                <td className="border border-stone-300 px-3 py-1.5 text-right font-mono">{fmtNum(totalSacos, 0)}</td>
                <td className="border border-stone-300 px-3 py-1.5 text-right font-mono">{fmtKg(pesoTeorico)}</td>
                <td className="border border-stone-300 px-3 py-1.5 text-right font-mono">100%</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Pesos + Quebra */}
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest text-purple-700 font-bold mb-2">Controle de Pesagem</div>
          <div className="grid grid-cols-3 gap-2">
            <div className="border-2 border-purple-500 rounded p-2 text-center bg-purple-50">
              <div className="text-[10px] uppercase tracking-wider text-purple-700 font-bold">Peso caminhão (entrada)</div>
              <div className="font-mono font-bold text-lg text-purple-800 mt-1" data-testid="peso-caminhao-display">
                {fmtKg(pesoCam)}
              </div>
            </div>
            <div className="border border-stone-300 rounded p-2 text-center">
              <div className="text-[10px] uppercase tracking-wider text-ink-500">Peso embalado ({fmtNum(totalSacos,0)} sc × 20kg)</div>
              <div className="font-mono font-bold text-lg text-ink-900 mt-1">{fmtKg(pesoTeorico)}</div>
            </div>
            <div className={`border-2 rounded p-2 text-center ${quebraPct > 10 ? "border-red-500 bg-red-50" : quebraPct > 5 ? "border-amber-500 bg-amber-50" : "border-emerald-500 bg-emerald-50"}`}>
              <div className={`text-[10px] uppercase tracking-wider font-bold ${quebraPct > 10 ? "text-red-700" : quebraPct > 5 ? "text-amber-700" : "text-emerald-700"}`}>% Quebra</div>
              <div className={`font-mono font-bold text-lg mt-1 ${quebraPct > 10 ? "text-red-800" : quebraPct > 5 ? "text-amber-800" : "text-emerald-800"}`} data-testid="quebra-pct">
                {quebraPct.toFixed(2)}%
              </div>
              <div className="text-[10px] text-ink-500 mt-0.5 font-mono">{fmtKg(quebraKg)}</div>
            </div>
          </div>
          <div className="text-[10px] text-ink-500 mt-1.5">
            Cálculo: peso pesado na chegada do caminhão (entrada) menos peso da mercadoria embalada em pallets.
          </div>
        </div>

        {/* Financeiro */}
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest text-purple-700 font-bold mb-2">Faturamento do Serviço</div>
          <table className="w-full text-sm border border-stone-300">
            <thead className="bg-purple-50">
              <tr>
                <th className="border border-stone-300 px-3 py-1.5 text-left text-[11px] uppercase tracking-wider text-purple-800">Descrição</th>
                <th className="border border-stone-300 px-3 py-1.5 text-right text-[11px] uppercase tracking-wider text-purple-800">Qtd (sc)</th>
                <th className="border border-stone-300 px-3 py-1.5 text-right text-[11px] uppercase tracking-wider text-purple-800">R$/sc</th>
                <th className="border border-stone-300 px-3 py-1.5 text-right text-[11px] uppercase tracking-wider text-purple-800">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-stone-300 px-3 py-1">Maquinação / Beneficiamento</td>
                <td className="border border-stone-300 px-3 py-1 text-right font-mono">{fmtNum(totalSacos, 0)}</td>
                <td className="border border-stone-300 px-3 py-1 text-right font-mono">{fmtBRL(precoServico)}</td>
                <td className="border border-stone-300 px-3 py-1 text-right font-mono font-bold" data-testid="valor-maquinacao">{fmtBRL(valorMaquinacao)}</td>
              </tr>
              {inv.usa_sacaria_propria && sacosSacariaPropria > 0 && (
                <tr>
                  <td className="border border-stone-300 px-3 py-1">Sacaria (fornecida pela empresa)</td>
                  <td className="border border-stone-300 px-3 py-1 text-right font-mono">{fmtNum(sacosSacariaPropria, 0)}</td>
                  <td className="border border-stone-300 px-3 py-1 text-right font-mono">{fmtBRL(precoSacaria)}</td>
                  <td className="border border-stone-300 px-3 py-1 text-right font-mono font-bold" data-testid="valor-sacaria">{fmtBRL(valorSacaria)}</td>
                </tr>
              )}
              <tr className="bg-purple-700 text-white font-black">
                <td className="border border-stone-300 px-3 py-2" colSpan={3}>TOTAL GERAL</td>
                <td className="border border-stone-300 px-3 py-2 text-right font-mono text-lg" data-testid="valor-total">{fmtBRL(valorTotal)}</td>
              </tr>
            </tbody>
          </table>
          <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
            <div className="border border-stone-300 rounded p-2 bg-stone-50">
              <span className="text-ink-500">Condição de pagamento:</span> <span className="font-semibold">15 dias</span>
            </div>
            <div className="border border-stone-300 rounded p-2 bg-stone-50">
              <span className="text-ink-500">Vencimento:</span> <span className="font-mono font-bold" data-testid="vencimento">{fmtDate(vencimento)}</span>
            </div>
          </div>
        </div>

        {/* Assinaturas */}
        <div className="mt-8 grid grid-cols-2 gap-6">
          <div className="text-center">
            <div className="border-t border-ink-700 pt-1 mx-4 text-[11px] text-ink-600">Assinatura Empresa</div>
            <div className="text-[10px] text-ink-400 mt-0.5">{company?.razao_social || company?.nome_fantasia}</div>
          </div>
          <div className="text-center">
            <div className="border-t border-ink-700 pt-1 mx-4 text-[11px] text-ink-600">Assinatura Cliente</div>
            <div className="text-[10px] text-ink-400 mt-0.5">{inv.cliente_dono}</div>
          </div>
        </div>

        {/* Rodapé */}
        <div className="mt-6 pt-3 border-t border-stone-300 text-[9px] text-ink-400 text-center">
          Documento não fiscal • Serve para controle interno e conferência do cliente na retirada da mercadoria beneficiada.
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
